import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Typography } from 'Components/atoms';
import { BsPlus } from '@react-icons/all-files/bs/BsPlus';
import { FiX } from '@react-icons/all-files/fi/FiX';
import { BsChevronDown } from '@react-icons/all-files/bs/BsChevronDown';
import { BsTrash2 } from '@react-icons/all-files/bs/BsTrash2';
import classNames from 'classnames';
import './styles.scss';
import { ComplianceTrainingSetting, ComplianceTrainingSettingSchema, genAmpId } from '@ampsec/platform-client';
import { CustomTooltip } from 'Components/atoms';
import { FiInfo } from '@react-icons/all-files/fi/FiInfo';
import { useI18n } from 'Hooks/useI18n';

// =============================================================================
// Constants & Options
// =============================================================================

const FREQUENCIES: ScheduleFrequency[] = ['monthly', 'quarterly', 'yearly'];
// Fallback minimum offset when the connector's sync interval is unavailable.
const SYNC_OFFSET_HOURS = 4;
const SYNC_OFFSET_SECONDS = SYNC_OFFSET_HOURS * 60 * 60;

const DAYS_OF_WEEK = [
    { value: 1, label: '1 - Monday' },
    { value: 2, label: '2 - Tuesday' },
    { value: 3, label: '3 - Wednesday' },
    { value: 4, label: '4 - Thursday' },
    { value: 5, label: '5 - Friday' },
    { value: 6, label: '6 - Saturday' },
    { value: 7, label: '7 - Sunday' },
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);

// =============================================================================
// Types
// =============================================================================

export type ScheduleFrequency = 'monthly' | 'quarterly' | 'yearly';
export type ScheduleStatus = 'ENABLED' | 'DISABLED';
export type TrainingScheduleRecord = ComplianceTrainingSetting['trainings'][number];

export interface ModuleTrainingData {
    description: string;
    videoUrl: string;
    thumbnailUrl: string;
    dueInDays?: number;
}

export interface ScheduleData {
    id: string;
    name: string;
    status: 'active' | 'disabled';
    frequency: ScheduleFrequency;
    dayOfMonth: number | null;
    dayOfWeek: number | null;
    nextRun: string; // YYYY-MM-DDTHH:mm (ISO format for input)
    lastRun: string | null; // ISO String
    isNew?: boolean; // true only for schedules not yet persisted to the backend
}

// =============================================================================
// Helpers
// =============================================================================

const getMinNextRunDate = (offsetSeconds: number = SYNC_OFFSET_SECONDS) => {
    // Advance by the connector's sync interval (or the fallback) so the chosen
    // next-run date is guaranteed to fall in a future sync window.
    const now = new Date(Date.now() + offsetSeconds * 1000);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getScheduleState = (schedule: ScheduleData) => {
    if (schedule.status === 'disabled') {
        return { label: 'Disabled', variant: 'disabled', isRunning: false };
    }

    // Newly created schedules have never been persisted and cannot be running on the backend.
    if (schedule.isNew) {
        return { label: 'Active', variant: 'active', isRunning: false };
    }

    // Compare epochs so the local datetime string (no timezone suffix) is interpreted
    // as local time rather than being lexicographically compared to a UTC ISO string.
    const isRunning = schedule.nextRun && new Date(schedule.nextRun).getTime() <= Date.now();

    if (isRunning) {
        return { label: 'Running', variant: 'running', isRunning: true };
    }

    return { label: 'Active', variant: 'active', isRunning: false };
};

// =============================================================================
// Converters
// =============================================================================

export const fromBackendRecord = (r: TrainingScheduleRecord): ScheduleData => ({
    id: r.id,
    name: r.name,
    status: r.status === 'ENABLED' ? 'active' : 'disabled',
    frequency: r.scheduleConfig.frequency as ScheduleFrequency,
    dayOfMonth: r.scheduleConfig.dayOfMonth ?? null,
    dayOfWeek: r.scheduleConfig.dayOfWeek ?? null,
    // Extract YYYY-MM-DDTHH:mm from ISO string for the input
    nextRun: r.nextRunAt ? r.nextRunAt.substring(0, 16) : '',
    lastRun: r.lastRunAt ?? null,
});

export const toBackendRecord = (
    // Destructure isNew out so it is never forwarded to the backend payload.
    { isNew: _isNew, ...local }: ScheduleData,
    subject: string,
    moduleData: ModuleTrainingData,
    existing?: TrainingScheduleRecord,
): TrainingScheduleRecord => ({
    id: local.id,
    name: local.name,
    description: existing?.description ?? moduleData.description,
    status: local.status === 'active' ? 'ENABLED' : 'DISABLED',
    trainingConfig: {
        subject,
        videoUrl: existing?.trainingConfig?.videoUrl ?? moduleData.videoUrl,
        thumbnailUrl: existing?.trainingConfig?.thumbnailUrl ?? moduleData.thumbnailUrl,
    },
    scheduleConfig: {
        frequency: local.frequency,
        dayOfMonth: local.dayOfMonth ?? null,
        dayOfWeek: local.dayOfWeek ?? null,
    },
    dueInDays: existing?.dueInDays ?? moduleData.dueInDays ?? null,
    lastRunAt: local.lastRun,
    // Ensure full ISO format for backend
    nextRunAt: local.nextRun ? `${local.nextRun}:00.000Z` : null,
});

// =============================================================================
// Components
// =============================================================================

const FrequencySelector = ({
    current,
    onChange,
    disabled,
}: {
    current: ScheduleFrequency;
    onChange: (f: ScheduleFrequency) => void;
    disabled?: boolean;
}) => (
    <div className={classNames('freq-selector', { disabled })}>
        {FREQUENCIES.map((freq) => (
            <button
                key={freq}
                type="button"
                className={classNames('freq-pill', { selected: current === freq })}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) {
                        onChange(freq);
                    }
                }}
                disabled={disabled}
            >
                {freq.charAt(0).toUpperCase() + freq.slice(1)}
            </button>
        ))}
    </div>
);

interface ScheduleSidePanelProps {
    isOpen: boolean;
    onClose: () => void;
    moduleName: string;
    moduleSubject: string;
    moduleData: ModuleTrainingData;
    allScheduleRecords: TrainingScheduleRecord[];
    isLoading?: boolean;
    onSave: (updatedRecords: TrainingScheduleRecord[]) => Promise<void>;
    /** Connector sync interval in seconds; used to compute the earliest selectable next-run date. */
    syncIntervalSeconds?: number;
}

export const ScheduleSidePanel: React.FC<ScheduleSidePanelProps> = ({
    isOpen,
    onClose,
    moduleName,
    moduleSubject,
    moduleData,
    allScheduleRecords,
    isLoading = false,
    onSave,
    syncIntervalSeconds,
}) => {
    const moduleRecords = useMemo(() => allScheduleRecords.filter((r) => r.trainingConfig?.subject === moduleSubject), [
        allScheduleRecords,
        moduleSubject,
    ]);
    const [schedules, setSchedules] = useState<ScheduleData[]>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [closeActionLoading, setCloseActionLoading] = useState(false);
    const [clampedScheduleIds, setClampedScheduleIds] = useState<Set<string>>(new Set());
    const baselineSigRef = useRef<string>('');
    const { t } = useI18n();
    const makeSig = useCallback((list: ScheduleData[]) => {
        const stable = [...list].sort((a, b) => a.id.localeCompare(b.id));
        return stable
            .map((s) => `${s.id}|${s.name}|${s.status}|${s.frequency}|${s.dayOfMonth}|${s.dayOfWeek}|${s.nextRun}`)
            .join('||');
    }, []);

    const hasUnsavedChanges = useMemo(() => makeSig(schedules) !== baselineSigRef.current, [schedules, makeSig]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const next = moduleRecords.map(fromBackendRecord);
        setSchedules(next);
        setExpandedId(null);
        setShowCloseConfirm(false);
        setCloseActionLoading(false);
        setClampedScheduleIds(new Set());
        baselineSigRef.current = makeSig(next);
    }, [isOpen, moduleRecords, makeSig]);

    const handleRequestClose = useCallback(() => {
        if (isSaving || closeActionLoading) {
            return;
        }
        if (hasUnsavedChanges) {
            setShowCloseConfirm(true);
            return;
        }
        onClose();
    }, [hasUnsavedChanges, onClose, isSaving, closeActionLoading]);

    const handleExpand = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

    // helper to update a schedule entry without nesting callbacks
    function updateScheduleList(list: ScheduleData[], id: string, patch: Partial<ScheduleData>) {
        return list.map((s) => (s.id === id ? { ...s, ...patch } : s));
    }

    const minDate = getMinNextRunDate(syncIntervalSeconds);

    const handleUpdate = useCallback((id: string, patch: Partial<ScheduleData>) => {
        setSchedules((prev) => updateScheduleList(prev, id, patch));
    }, []);

    const handleNextRunChange = useCallback((id: string, value: string) => {
        const isBelowMin = value < minDate;
        handleUpdate(id, { nextRun: isBelowMin ? minDate : value });
        setClampedScheduleIds((prev) => {
            const next = new Set(prev);
            isBelowMin ? next.add(id) : next.delete(id);
            return next;
        });
    }, [minDate, handleUpdate]);

    const handleAdd = () => {
        const newId = genAmpId();
        const now = new Date();
        const newSchedule: ScheduleData = {
            id: newId,
            name: 'New Schedule',
            status: 'active',
            frequency: 'monthly',
            dayOfMonth: now.getDate(),
            dayOfWeek: now.getDay() === 0 ? 7 : now.getDay(),
            nextRun: '',
            lastRun: null,
            isNew: true,
        };
        setSchedules((prev) => [newSchedule, ...prev]);
        setExpandedId(newId);
    };

    const handleDelete = (id: string) => {
        setSchedules((prev) => prev.filter((s) => s.id !== id));
        if (expandedId === id) {
            setExpandedId(null);
        }
    };

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            const otherRecords = allScheduleRecords.filter((r) => r.trainingConfig?.subject !== moduleSubject);
            const existingMap: Record<string, TrainingScheduleRecord | undefined> = {};
            moduleRecords.forEach((r) => {
                existingMap[r.id] = r;
            });
            const updatedModuleRecords: TrainingScheduleRecord[] = schedules.map((local) => {
                const existing = existingMap[local.id];
                return toBackendRecord(local, moduleSubject, moduleData, existing);
            });
            const fullPayload = [...otherRecords, ...updatedModuleRecords];
            const validation = ComplianceTrainingSettingSchema.shape.trainings.safeParse(fullPayload);

            if (validation.success) {
                await onSave(validation.data);
            } else {
                await onSave(fullPayload);
            }
            baselineSigRef.current = makeSig(schedules);
        } finally {
            setIsSaving(false);
        }
    }, [allScheduleRecords, moduleSubject, moduleData, schedules, moduleRecords, onSave, makeSig]);

    const handleKeepEditing = () => setShowCloseConfirm(false);
    const handleDiscardAndClose = () => {
        setShowCloseConfirm(false);
        onClose();
    };
    const handleSaveAndClose = async () => {
        setCloseActionLoading(true);
        try {
            await handleSave();
            setShowCloseConfirm(false);
            onClose();
        } finally {
            setCloseActionLoading(false);
        }
    };

    const activeCount = schedules.filter((s) => s.status === 'active').length;

    return (
        <>
            <div
                className={classNames('ssp-backdrop', { open: isOpen })}
                onClick={handleRequestClose}
                aria-hidden="true"
            />
            <div
                className={classNames('ssp-panel', { open: isOpen })}
                role="dialog"
                aria-modal="true"
                aria-label={`Manage schedules for ${moduleName}`}
            >
                <div className="ssp-header">
                    <div className="ssp-header__text">
                        <Typography
                            variant="caption1"
                            color="text-medium-emphasis"
                            className="text-uppercase ssp-header__label"
                        >
                            Manage Schedules
                        </Typography>
                        <Typography variant="subtitle3" color="text-high-emphasis" className="ssp-header__subtitle">
                            {moduleName}
                        </Typography>
                    </div>
                    <button className="ssp-close-btn" onClick={handleRequestClose} type="button" aria-label="Close">
                        <FiX size={18} />
                    </button>
                </div>

                <div className="ssp-toolbar">
                    <Typography variant="body3" color="text-medium-emphasis">
                        {activeCount} Active Schedule{activeCount !== 1 ? 's' : ''}
                    </Typography>
                    <button className="ssp-add-btn" onClick={handleAdd} type="button" disabled={showCloseConfirm}>
                        <BsPlus size={16} /> Add
                    </button>
                </div>

                <div className="ssp-body">
                    {isLoading ? (
                        <div className="ssp-loading">
                            <Spinner animation="border" size="sm" />
                            <Typography variant="body3" color="text-medium-emphasis">
                                Loading schedules...
                            </Typography>
                        </div>
                    ) : (
                        <>
                            {schedules.map((schedule) => {
                                const isExpanded = expandedId === schedule.id;
                                const { label: statusLabel, variant: statusVariant, isRunning } = getScheduleState(
                                    schedule,
                                );
                                const isLocked = isRunning;

                                return (
                                    <div key={schedule.id} className={classNames('ssp-card', { expanded: isExpanded })}>
                                        <div
                                            className="ssp-card__summary"
                                            onClick={() => {
                                                if (!showCloseConfirm) {
                                                    handleExpand(schedule.id);
                                                }
                                            }}
                                        >
                                            <div className={classNames('ssp-card__chevron', { rotate: isExpanded })}>
                                                <BsChevronDown size={11} />
                                            </div>
                                            <div className="ssp-card__info">
                                                {isExpanded ? (
                                                    <input
                                                        className="ssp-card__name-input"
                                                        value={schedule.name}
                                                        onChange={(e) =>
                                                            handleUpdate(schedule.id, { name: e.target.value })
                                                        }
                                                        onClick={(e) => e.stopPropagation()}
                                                        disabled={showCloseConfirm || isLocked}
                                                    />
                                                ) : (
                                                    <div className="ssp-card__name-row">
                                                        <span className="ssp-card__name">{schedule.name}</span>
                                                        <span className={classNames('ssp-status-pill', statusVariant)}>
                                                            {statusLabel}
                                                        </span>
                                                    </div>
                                                )}

                                                {!isExpanded && (
                                                    <div className="ssp-card__meta">
                                                        <div className="meta-group">
                                                            <span
                                                                className="meta-value"
                                                                style={{ textTransform: 'capitalize' }}
                                                            >
                                                                {schedule.frequency}
                                                            </span>
                                                        </div>
                                                        <span className="meta-divider">|</span>
                                                        <div className="meta-group">
                                                            <span className="meta-label">Next:</span>
                                                            <span className="meta-value">
                                                                {schedule.nextRun
                                                                    ? new Date(schedule.nextRun).toLocaleString()
                                                                    : 'Auto'}
                                                            </span>
                                                        </div>
                                                        <span className="meta-divider">|</span>
                                                        <div className="meta-group">
                                                            <span className="meta-label">Last:</span>
                                                            <span className="meta-value">
                                                                {schedule.lastRun
                                                                    ? new Date(schedule.lastRun).toLocaleDateString()
                                                                    : 'Never'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {!isExpanded && (
                                                <div className="ssp-card__toggle" onClick={(e) => e.stopPropagation()}>
                                                    <Form.Check
                                                        type="switch"
                                                        checked={schedule.status === 'active'}
                                                        onChange={(e) =>
                                                            handleUpdate(schedule.id, {
                                                                status: e.target.checked ? 'active' : 'disabled',
                                                            })
                                                        }
                                                        disabled={showCloseConfirm}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {isExpanded && (
                                            <div className="ssp-card__editor">
                                                {isLocked && (
                                                    <div className="ssp-warning-box">
                                                        <Typography variant="body4">
                                                            This schedule is currently processing or running.
                                                            Configuration is locked, but you can still disable it.
                                                        </Typography>
                                                    </div>
                                                )}

                                                {/* STATUS - UNLOCKED */}
                                                <div className="ssp-field">
                                                    <span className="ssp-field__label">Status</span>
                                                    <div
                                                        className={classNames('ssp-status-box', {
                                                            active: schedule.status === 'active',
                                                        })}
                                                    >
                                                        <span className="ssp-status-box__label">
                                                            {schedule.status === 'active' ? 'Active' : 'Disabled'}
                                                        </span>
                                                        <Form.Check
                                                            type="switch"
                                                            checked={schedule.status === 'active'}
                                                            onChange={(e) =>
                                                                handleUpdate(schedule.id, {
                                                                    status: e.target.checked ? 'active' : 'disabled',
                                                                })
                                                            }
                                                            disabled={showCloseConfirm}
                                                        />
                                                    </div>
                                                </div>

                                                {/* LOCKED CONFIGURATION */}
                                                <div className={classNames('ssp-locked-content', { locked: isLocked })}>
                                                    <div className="ssp-field">
                                                        <span className="ssp-field__label">Frequency</span>
                                                        <FrequencySelector
                                                            current={schedule.frequency}
                                                            onChange={(freq) =>
                                                                handleUpdate(schedule.id, { frequency: freq })
                                                            }
                                                            disabled={isLocked}
                                                        />
                                                    </div>

                                                    <div className="ssp-field-row">
                                                        <div className="ssp-field">
                                                            <span className="ssp-field__label">Day of Month</span>
                                                            <div className="position-relative">
                                                                <Form.Select
                                                                    className="ssp-select"
                                                                    size="sm"
                                                                    value={schedule.dayOfMonth ?? ''}
                                                                    onChange={(e) =>
                                                                        handleUpdate(schedule.id, {
                                                                            dayOfMonth: e.target.value
                                                                                ? parseInt(e.target.value)
                                                                                : null,
                                                                        })
                                                                    }
                                                                    disabled={showCloseConfirm || isLocked}
                                                                >
                                                                    <option value="" style={{ color: 'black' }}>
                                                                        Select Day
                                                                    </option>
                                                                    {DAYS_OF_MONTH.map((day) => (
                                                                        <option
                                                                            key={day}
                                                                            value={day}
                                                                            style={{ color: 'black' }}
                                                                        >
                                                                            {day}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                                <BsChevronDown
                                                                    size={12}
                                                                    color="#ffffff"
                                                                    style={{
                                                                        position: 'absolute',
                                                                        right: '0.75rem',
                                                                        top: '50%',
                                                                        transform: 'translateY(-50%)',
                                                                        pointerEvents: 'none',
                                                                        opacity: 0.8,
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="ssp-field">
                                                            <span className="ssp-field__label">Day of Week</span>
                                                            <div className="position-relative">
                                                                <Form.Select
                                                                    className="ssp-select"
                                                                    size="sm"
                                                                    value={schedule.dayOfWeek ?? ''}
                                                                    onChange={(e) =>
                                                                        handleUpdate(schedule.id, {
                                                                            dayOfWeek: e.target.value
                                                                                ? parseInt(e.target.value)
                                                                                : null,
                                                                        })
                                                                    }
                                                                    disabled={showCloseConfirm || isLocked}
                                                                >
                                                                    <option value="" style={{ color: 'black' }}>
                                                                        Select Day
                                                                    </option>
                                                                    {DAYS_OF_WEEK.map((day) => (
                                                                        <option
                                                                            key={day.value}
                                                                            value={day.value}
                                                                            style={{ color: 'black' }}
                                                                        >
                                                                            {day.label}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                                <BsChevronDown
                                                                    size={12}
                                                                    color="#ffffff"
                                                                    style={{
                                                                        position: 'absolute',
                                                                        right: '0.75rem',
                                                                        top: '50%',
                                                                        transform: 'translateY(-50%)',
                                                                        pointerEvents: 'none',
                                                                        opacity: 0.8,
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="ssp-field">
                                                        <span className="ssp-field__label">
                                                          Earliest Next Run
                                                          <CustomTooltip label={t('COMPLIANCE_TRAINING_SCHEDULE_MIN_NEXT_RUN_TOOLTIP')}>
                                                            <FiInfo size={12} />
                                                          </CustomTooltip>
                                                        </span>
                                                        <input
                                                            type="datetime-local"
                                                            className={classNames('ssp-date-input', { invalid: clampedScheduleIds.has(schedule.id) })}
                                                            min={minDate}
                                                            value={schedule.nextRun}
                                                            onFocus={(e) => e.target.showPicker()}
                                                            onChange={(e) => handleNextRunChange(schedule.id, e.target.value)}
                                                            disabled={showCloseConfirm || isLocked}
                                                        />
                                                        {clampedScheduleIds.has(schedule.id) && (
                                                            <span className="ssp-field-hint">
                                                                Next run date can be no earlier than:{' '}
                                                                {new Date(minDate).toLocaleString(undefined, {
                                                                    dateStyle: 'medium',
                                                                    timeStyle: 'short',
                                                                })}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="ssp-card__actions">
                                                        <button
                                                            type="button"
                                                            className="ssp-delete-btn"
                                                            onClick={() => handleDelete(schedule.id)}
                                                            disabled={showCloseConfirm || isLocked}
                                                        >
                                                            <BsTrash2 size={13} /> Delete
                                                        </button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            onClick={() => setExpandedId(null)}
                                                            disabled={showCloseConfirm}
                                                        >
                                                            Done
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {schedules.length === 0 && (
                                <div className="ssp-empty">
                                    <Typography variant="body3" color="text-medium-emphasis">
                                        No schedules configured yet.
                                    </Typography>
                                    <Typography variant="caption1" color="text-medium-emphasis">
                                        Click "+ Add" above to create one.
                                    </Typography>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="ssp-footer">
                    <Button
                        variant="primary"
                        className="w-100"
                        onClick={handleSave}
                        disabled={isSaving || isLoading || showCloseConfirm}
                    >
                        {isSaving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" /> Saving...
                            </>
                        ) : (
                            'Save All Changes'
                        )}
                    </Button>
                </div>

                {showCloseConfirm && (
                    <div className="ssp-confirm-layer" role="presentation">
                        <div
                            className="ssp-confirm-card"
                            role="alertdialog"
                            aria-modal="true"
                            aria-label="Unsaved changes"
                        >
                            <Typography variant="subtitle3" color="text-high-emphasis">
                                Unsaved changes
                            </Typography>
                            <Typography variant="body3" color="text-medium-emphasis" className="mt-1">
                                You have changes that are not saved yet. Choose what to do before closing.
                            </Typography>
                            <div className="ssp-confirm-actions">
                                <Button
                                    variant="outline-light"
                                    onClick={handleKeepEditing}
                                    disabled={closeActionLoading || isSaving}
                                >
                                    Keep editing
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    onClick={handleDiscardAndClose}
                                    disabled={closeActionLoading || isSaving}
                                >
                                    Discard
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleSaveAndClose}
                                    disabled={closeActionLoading || isSaving || isLoading}
                                >
                                    {closeActionLoading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" /> Saving...
                                        </>
                                    ) : (
                                        'Save and close'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
